import { getLocalStorage, setLocalStorage } from '@/utils/localStorage';
import md5 from 'js-md5';
import {
  LOGIN_TRAFFIC_SOURCE_KEY,
  LOGIN_SOURCE_BAIDULK,
  LOGIN_SOURCE_MSCLK,
  LOGIN_SOURCE_YKLK,
  LOGIN_SOURCE_SOUGOULK,
  LOGIN_SOURCE_360LK,
  LOGIN_LAST_LOCAL_DAY_KEY,
  LOGIN_RETENTION_REPORTED_KEY,
  LOGIN_360_PROMOTION_PARAMS_KEY
} from './types';
import {
  BaiduBusinessIndustrValue,
  buryOptsForUid,
  callBaiduBusinessUpload,
  isBaiduBusinessUploadAvailable
} from './baiduBusinessReport';

const INDUSTRY_DISPLAY_AD: BaiduBusinessIndustrValue = 'ocpc_zs_convert';
const INDUSTRY_PC_SEARCH: BaiduBusinessIndustrValue = 'ocpc_ps_convert';

/**
 * 与 setTrafficSourceFromHisId 一致：reportedId 末段为 1 且子渠道非 msclk/yklk/sougoulk 时视为 360 推广（360lk）。
 * 用于写入 login360PromotionParams 等，勿与仅 SATpczs 的投放 id 混淆。
 */
export function is360LkPromotionFromHisId(hisId: string): boolean {
  const trimmed = String(hisId || '').trim();
  if (!trimmed) return false;
  const paramArr = trimmed.split('#');
  const reportedId = paramArr[paramArr.length - 1] ? paramArr[paramArr.length - 1] : '';
  if (reportedId !== '1') return false;
  const ch = paramArr[1];
  if (ch && (ch === 'msclk' || ch === 'yklk' || ch === 'sougoulk')) return false;
  return true;
}

/** 根据 hisId 解析并写入 LOGIN_TRAFFIC_SOURCE_KEY；非 360 推广时清空 login360PromotionParams。请在账号登录页按外壳 REPORTED_KEY 同步后调用，其它页勿重复写来源 */
export function setTrafficSourceFromHisId(hisId: string) {
  if (!hisId || !String(hisId).trim()) {
    setLocalStorage(LOGIN_TRAFFIC_SOURCE_KEY, '');
    setLocalStorage(LOGIN_360_PROMOTION_PARAMS_KEY, '');
    return;
  }
  const paramArr = hisId.split('#');
  const reportedId = paramArr[paramArr.length - 1] ? paramArr[paramArr.length - 1] : '';
  if (reportedId !== '1') {
    setLocalStorage(LOGIN_TRAFFIC_SOURCE_KEY, LOGIN_SOURCE_BAIDULK);
    setLocalStorage(LOGIN_360_PROMOTION_PARAMS_KEY, '');
    return;
  }
  const ch = paramArr[1];
  if (ch && ch === 'msclk') {
    setLocalStorage(LOGIN_TRAFFIC_SOURCE_KEY, LOGIN_SOURCE_MSCLK);
    setLocalStorage(LOGIN_360_PROMOTION_PARAMS_KEY, '');
    return;
  }
  if (ch && ch === 'yklk') {
    setLocalStorage(LOGIN_TRAFFIC_SOURCE_KEY, LOGIN_SOURCE_YKLK);
    setLocalStorage(LOGIN_360_PROMOTION_PARAMS_KEY, '');
    return;
  }
  if (ch && ch === 'sougoulk') {
    setLocalStorage(LOGIN_TRAFFIC_SOURCE_KEY, LOGIN_SOURCE_SOUGOULK);
    setLocalStorage(LOGIN_360_PROMOTION_PARAMS_KEY, '');
    return;
  }
  setLocalStorage(LOGIN_TRAFFIC_SOURCE_KEY, LOGIN_SOURCE_360LK);
}

function formatLocalYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isExactlyNextLocalDay(prevYmd: string, now: Date): boolean {
  if (!prevYmd || !/^\d{4}-\d{2}-\d{2}$/.test(prevYmd)) return false;
  const [py, pm, pd] = prevYmd.split('-').map(Number);
  const prev = new Date(py, pm - 1, pd);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return today.getTime() - prev.getTime() === 86400000;
}

/**
 * 次日留存（本机维度）：读 LOGIN_TRAFFIC_SOURCE_KEY（须由账号登录初始页 getReported 写入）+ hisId 末段 1；
 * 本机仅上报一次；仅「首次登录日」的次日再次登录才上报。首次进入时只记录首登日期，后续不覆盖。
 */
export function applyBaidu360RetentionOnLogin(hisId: string, buryUid?: string | number) {
  const firstLoginYmd = getLocalStorage(LOGIN_LAST_LOCAL_DAY_KEY);
  const alreadyReported = getLocalStorage(LOGIN_RETENTION_REPORTED_KEY) === '1';
  const today = new Date();
  const todayYmd = formatLocalYmd(today);

  // 首次登录仅记录日期，不做次留上报判断
  if (!firstLoginYmd) {
    setLocalStorage(LOGIN_LAST_LOCAL_DAY_KEY, todayYmd);
    return;
  }

  let is360Eligible = false;
  let paramArr: string[] = [];
  if (hisId && isBaiduBusinessUploadAvailable()) {
    const loginTrafficSource = getLocalStorage(LOGIN_TRAFFIC_SOURCE_KEY);
    paramArr = hisId.split('#');
    const reportedId = paramArr[paramArr.length - 1] ? paramArr[paramArr.length - 1] : '';
    is360Eligible = loginTrafficSource === LOGIN_SOURCE_360LK && reportedId === '1';
  }

  const canReportRetention = is360Eligible && !alreadyReported && isExactlyNextLocalDay(firstLoginYmd, today);

  if (canReportRetention) {
    const hasJzqs = paramArr.length >= 5 && Boolean(paramArr[3]);
    const industr = hasJzqs ? INDUSTRY_DISPLAY_AD : INDUSTRY_PC_SEARCH;
    callBaiduBusinessUpload(
      {
        businessInfo: 'RETENTION',
        businessType: '1',
        industr,
        businessParam: {
          trans_id: md5(String(Date.now()))
        }
      },
      {
        logLabel: 'RETENTION 次日留存',
        logExtra: {
          hisId,
          firstLoginDay: firstLoginYmd,
          todayYmd,
          paramArr,
          loginTrafficSource: getLocalStorage(LOGIN_TRAFFIC_SOURCE_KEY)
        },
        ...buryOptsForUid(buryUid)
      }
    );
    setLocalStorage(LOGIN_RETENTION_REPORTED_KEY, '1');
  }
}
